import { Injectable } from "@angular/core";
import { FormControl, Validators, AbstractControl, ValidationErrors } from "@angular/forms";
import * as moment from 'moment';
import { HasLetterOrDigit, HasDigit, HasLower, HasUpper, DistinctChars } from "../function/helper.function";
import { NricService } from "../../../app/shared/service/nric.service";

@Injectable()
export class FormValidators {
  static nricValidator(minAge?: number) {
    return (control: AbstractControl) => {
      if (
        Validators.required(control) !== undefined &&
        Validators.required(control) !== null
      )
        return null;
      let validatorsPattern = /\d{6}-\d{2}-\d{4}/g;
      let nric: string = control.value || "";
      let status = { nric: true, msg: "" };
      if (!nric.match(validatorsPattern)) {
        status.msg = "Invalid NRIC format";
        return status;
      }

      let nricYear = nric.substring(0, 2);
      let nricMonth = nric.substring(2, 4);
      let nricDay = nric.substring(4, 6);
      let strDate = `${nricYear}-${nricMonth}-${nricDay}`;
      if (!moment(strDate, "YY-MM-DD").isValid()) {
        status.msg = "Invalid date";
        return status;
      }

      if (minAge) {
        const nricFormat = new NricService(nric);
        if (!nricFormat.valid) {
          status.msg = "Invalid NRIC format";
          return status;
        }
        let age = nricFormat.age;
        if (age < minAge) {
          status.msg = `Minimum age is ${minAge} Please
        enter a valid D.O.B.`;
          return status;
        }
      }

      return null;
    };
  }

  static minAgeValidator(minAge: number) {
    return (control: AbstractControl) => {
      if (
        Validators.required(control) !== undefined &&
        Validators.required(control) !== null
      )
        return null;

      let date = moment(control.value);

      if (!date.isValid()) return null;

      let age = moment().diff(date, "years");
      if (age < minAge)
        return {
          minAge: true,
          msg: `Minimum age is ${minAge} Please
      enter a valid D.O.B.`,
        };
    };
  }
  static passportValidator(control: FormControl) {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;
    let validatorsPattern = "^([a-zA-Z0-9]){7,14}$";
    let value: string = control.value || '';
    return value.match(validatorsPattern) ? null : { 'passport': true };
  }

  static positiveValidator(control: FormControl) {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;
    if (Math.sign(Number(control.value)) != 1) {
      return { positive: true };
    } else {
      return null;
    }
  }

  static userNameValidator(control: FormControl) {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;
    let validatorsPattern = "^[A-Za-z0-9@.]*$";
    let value: string = control.value || '';
    return value.match(validatorsPattern) ? null : { 'userName': true };
  }


  static forbiddenObjectValidator(control: FormControl) {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;
    return typeof control.value !== 'object' || control.value === null ? { 'forbiddenObject': true } : null;
  }

  static PasswordValidator = {
    RequiredLength: (minLength: number) => {
      return Validators.minLength(minLength);
    },
    RequireNonAlphanumeric: (error?: string) => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;

        return HasLetterOrDigit(control.value)
          ? { "RequireNonAlphanumeric": { "invalid": true, "error": error } }
          : null;
      };
    },
    RequireDigit: (error?: string) => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;

        return HasDigit(control.value)
          ? null
          : { "RequireDigit": { "invalid": true, "error": error } };
      };
    },
    RequireLowercase: (error?: string) => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;

        return HasLower(control.value)
          ? null
          : { "RequireLowercase": { "invalid": true, "error": error } };
      };
    },
    RequireUppercase: (error?: string) => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;

        return HasUpper(control.value)
          ? null
          : { "RequireUppercase": { "invalid": true, "error": error } };
      };
    },
    RequiredUniqueChars: (requiredCharCount: number, error?: string) => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (Validators.required(control) !== undefined && Validators.required(control) !== null) return null;

        let uniqueCount = DistinctChars(control.value).length;
        return uniqueCount < requiredCharCount
          ? { "RequiredUniqueChars": { "invalid": true, "minChar": requiredCharCount, "actual": uniqueCount, "error": error } }
          : null;
      };
    },
    GetErrorMessage: (control: AbstractControl, errorMessages: any[]) => {
      if (control.hasError("required")) {
        return "{0} is required.";
      }
      if (control.hasError("minlength")) {
        return errorMessages.find(x => x.code == "PasswordTooShort").description.replace("'{0}'", control.errors.minlength.requiredLength);
      }
      if (control.hasError("RequireDigit")) {
        return errorMessages.find(x => x.code == "PasswordRequiresDigit").description;
      }
      if (control.hasError("RequireLowercase")) {
        return errorMessages.find(x => x.code == "PasswordRequiresLower").description;
      }
      if (control.hasError("RequireUppercase")) {
        return errorMessages.find(x => x.code == "PasswordRequiresUpper").description;
      }
      if (control.hasError("RequireNonAlphanumeric")) {
        return errorMessages.find(x => x.code == "PasswordRequiresNonAlphanumeric").description;
      }
      if (control.hasError("RequiredUniqueChars")) {
        return errorMessages.find(x => x.code == "PasswordRequiresUniqueChars").description.replace("'{0}'", control.errors.RequiredUniqueChars.minChar);
      }

      return "";
    }
  };
}